import { useState, useEffect, useCallback } from "react";
import { Cloud, KeyRound, Plug, PackageSearch, RefreshCw, Trash2, CheckCircle2, XCircle, ScrollText, Plus, Pencil, Star, Copy, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function ResultViewer({ title, result }) {
  if (!result) return null;
  const ok = result.ok;
  return (
    <div className="mt-4 rounded-md border border-border bg-secondary/40" data-testid="ep-result">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
        {ok ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        ) : (
          <XCircle className="h-4 w-4 text-destructive" />
        )}
        <span className="text-sm font-medium">{title}</span>
        <Badge variant={ok ? "secondary" : "destructive"} className="ml-auto">
          {ok ? "Success" : "Error"}
          {result.http_status ? ` · HTTP ${result.http_status}` : ""}
        </Badge>
      </div>
      {result.error && (
        <p className="px-4 py-2 text-sm text-destructive" data-testid="ep-result-error">{result.error}</p>
      )}
      {result.note && (
        <p className="px-4 py-2 text-xs text-muted-foreground border-t border-border" data-testid="ep-result-note">{result.note}</p>
      )}
      <pre className="max-h-96 overflow-auto px-4 py-3 text-xs font-mono leading-relaxed whitespace-pre-wrap break-all">
        {JSON.stringify(result.data ?? result.raw ?? {}, null, 2)}
      </pre>
    </div>
  );
}

export default function ExternalProvider() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";

  const [config, setConfig] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [providerName, setProviderName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [streamDns, setStreamDns] = useState("");
  const [streamProtocol, setStreamProtocol] = useState("http");
  const [streamPort, setStreamPort] = useState("");
  const [streamMode, setStreamMode] = useState("proxy");
  const [templates, setTemplates] = useState([]);
  const [tplForm, setTplForm] = useState({ id: "", name: "", pack_id: "" });
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [bouquets, setBouquets] = useState(null);
  const [logs, setLogs] = useState([]);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/external-provider/config");
      setConfig(data);
      setBaseUrl(data.base_url || "");
      setProviderName(data.name || "");
      setEnabled(!!data.enabled);
      setStreamDns(data.stream_dns || "");
      setStreamProtocol(data.stream_protocol || "http");
      setStreamPort(data.stream_port || "");
      setStreamMode(data.stream_mode || "proxy");
      setTemplates(data.templates || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const { data } = await api.get("/external-provider/logs?limit=30");
      setLogs(data.logs || []);
    } catch {}
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await api.get("/external-provider/accounts");
      setAccounts(data.accounts || []);
    } catch {}
  }, []);

  const saveTemplates = async (list) => {
    try {
      const { data } = await api.put("/external-provider/config", { templates: list });
      setTemplates(data.templates || []);
      toast.success("Templates saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const addTemplate = () => {
    if (!tplForm.name.trim() || !tplForm.pack_id.trim()) return toast.error("Name and Package ID required");
    let list;
    if (tplForm.id) {
      list = templates.map((t) => (t.id === tplForm.id ? { ...t, name: tplForm.name.trim(), pack_id: tplForm.pack_id.trim() } : t));
    } else {
      list = [...templates, { name: tplForm.name.trim(), pack_id: tplForm.pack_id.trim(), is_default: templates.length === 0 }];
    }
    setTplForm({ id: "", name: "", pack_id: "" });
    saveTemplates(list);
  };

  const editTemplate = (t) => setTplForm({ id: t.id, name: t.name, pack_id: t.pack_id });
  const deleteTemplate = (id) => saveTemplates(templates.filter((t) => t.id !== id));
  const makeDefault = (id) => saveTemplates(templates.map((t) => ({ ...t, is_default: t.id === id })));

  useEffect(() => {
    loadConfig();
    loadLogs();
    loadAccounts();
  }, [loadConfig, loadLogs, loadAccounts]);

  const saveConfig = async () => {
    setBusy("config");
    try {
      const { data } = await api.put("/external-provider/config", {
        base_url: baseUrl, enabled, name: providerName,
        stream_dns: streamDns, stream_protocol: streamProtocol, stream_port: streamPort,
        stream_mode: streamMode,
      });
      setConfig(data);
      toast.success("Configuration saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const saveApiKey = async () => {
    if (!apiKey.trim()) return toast.error("Enter an API key first");
    setBusy("key");
    try {
      const { data } = await api.put("/external-provider/api-key", { api_key: apiKey.trim() });
      setConfig(data);
      setApiKey("");
      toast.success("API key stored securely");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const clearApiKey = async () => {
    setBusy("clear");
    try {
      const { data } = await api.delete("/external-provider/api-key");
      setConfig(data);
      setEnabled(false);
      toast.success("API key cleared");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const testConnection = async () => {
    setBusy("test");
    setTestResult(null);
    try {
      const { data } = await api.post("/external-provider/test-connection");
      setTestResult(data);
      data.ok ? toast.success("Connection OK") : toast.error(data.error || "Connection failed");
      loadLogs();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const getBouquets = async () => {
    setBusy("bouquets");
    setBouquets(null);
    try {
      const { data } = await api.get("/external-provider/bouquets");
      setBouquets(data);
      data.ok ? toast.success(data.empty ? "No custom bouquets" : "Custom bouquets loaded") : toast.error(data.error || "Failed to load custom bouquets");
      loadLogs();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground" data-testid="ep-loading">Loading external provider…</div>;
  }

  return (
    <div data-testid="external-provider-page">
      <PageHeader
        overline="Integrations"
        title="External Provider"
        description="Connect an external IPTV reseller panel (ActivationPanel). Your API key is stored encrypted on the server and never exposed to the browser."
      >
        <Badge variant={config?.enabled ? "secondary" : "outline"} data-testid="ep-status-badge">
          {config?.enabled ? "Enabled" : "Disabled"}
        </Badge>
      </PageHeader>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Configuration */}
        <Card data-testid="ep-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><Cloud className="h-5 w-5 text-primary" /> Configuration</CardTitle>
            <CardDescription>Provider endpoint and activation state.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ep-name">Provider name</Label>
              <Input
                id="ep-name"
                data-testid="ep-name-input"
                value={providerName}
                onChange={(e) => setProviderName(e.target.value)}
                disabled={!isAdmin}
                placeholder="ActivationPanel"
              />
              <p className="text-xs text-muted-foreground">Shown in the New Customer provider selector.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ep-base-url">API Base URL</Label>
              <Input
                id="ep-base-url"
                data-testid="ep-base-url-input"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                disabled={!isAdmin}
                placeholder="https://activationpanel.net/api/api.php"
              />
            </div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <div>
                <p className="text-sm font-medium">Enable provider</p>
                <p className="text-xs text-muted-foreground">Required before any request is sent.</p>
              </div>
              <Switch data-testid="ep-enabled-switch" checked={enabled} onCheckedChange={setEnabled} disabled={!isAdmin} />
            </div>
            <div className="rounded-md border border-border p-3 space-y-3">
              <div>
                <p className="text-sm font-medium">Streaming DNS</p>
                <p className="text-xs text-muted-foreground">Playback host used to build customer M3U links. Masked behind your StreamCore DNS.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ep-stream-dns">Host / DNS</Label>
                <Input id="ep-stream-dns" data-testid="ep-stream-dns-input" value={streamDns} onChange={(e) => setStreamDns(e.target.value)} disabled={!isAdmin} placeholder="line.example.com" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Protocol</Label>
                  <Select value={streamProtocol} onValueChange={setStreamProtocol} disabled={!isAdmin}>
                    <SelectTrigger data-testid="ep-stream-protocol"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      <SelectItem value="http">http</SelectItem>
                      <SelectItem value="https">https</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ep-stream-port">Port</Label>
                  <Input id="ep-stream-port" data-testid="ep-stream-port-input" value={streamPort} onChange={(e) => setStreamPort(e.target.value)} disabled={!isAdmin} placeholder="80" />
                </div>
              </div>
            </div>
            <div className="rounded-md border border-border p-3 space-y-3">
              <div>
                <p className="text-sm font-medium">Stream delivery mode</p>
                <p className="text-xs text-muted-foreground">
                  How channel streams reach the player. Use <b>Direct</b> if channels keep playing the first
                  selected channel (common with connection-limited providers, e.g. max_connections=1).
                </p>
              </div>
              <Select value={streamMode} onValueChange={setStreamMode} disabled={!isAdmin}>
                <SelectTrigger data-testid="ep-stream-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover">
                  <SelectItem value="proxy">Proxy (mask DNS — default)</SelectItem>
                  <SelectItem value="redirect">Direct redirect (reliable channel switching)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                {streamMode === "redirect"
                  ? "Player connects directly to the provider stream per channel — fixes “same channel every time”. The provider host may be visible at the transport layer during playback (credentials are never in the link)."
                  : "Streams are proxied through StreamCore so the provider host stays fully hidden."}
              </p>
            </div>
            {isAdmin && (
              <Button onClick={saveConfig} disabled={busy === "config"} data-testid="ep-save-config-btn">
                {busy === "config" ? "Saving…" : "Save configuration"}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* API key */}
        <Card data-testid="ep-key-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><KeyRound className="h-5 w-5 text-primary" /> Reseller API Key</CardTitle>
            <CardDescription>Stored encrypted (AES/Fernet). Only the last 4 characters are ever shown.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2.5">
              <span className="text-sm text-muted-foreground">Current:</span>
              {config?.has_api_key ? (
                <code className="text-sm font-mono" data-testid="ep-key-hint">{config.api_key_hint}</code>
              ) : (
                <span className="text-sm text-muted-foreground" data-testid="ep-key-hint">Not set</span>
              )}
            </div>
            {isAdmin ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="ep-api-key">{config?.has_api_key ? "Replace API key" : "Set API key"}</Label>
                  <Input
                    id="ep-api-key"
                    data-testid="ep-api-key-input"
                    type="password"
                    autoComplete="off"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="Paste your reseller API key"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveApiKey} disabled={busy === "key"} data-testid="ep-save-key-btn">
                    {busy === "key" ? "Saving…" : "Save API key"}
                  </Button>
                  {config?.has_api_key && (
                    <Button variant="outline" onClick={clearApiKey} disabled={busy === "clear"} data-testid="ep-clear-key-btn">
                      <Trash2 className="h-4 w-4 mr-1.5" /> Clear
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Only administrators can manage the API key.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card className="mt-6" data-testid="ep-actions-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Plug className="h-5 w-5 text-primary" /> Actions</CardTitle>
          <CardDescription>Read-only checks. No customers are created or modified on the external provider.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button onClick={testConnection} disabled={busy === "test" || !config?.has_api_key} data-testid="ep-test-btn">
              <Plug className="h-4 w-4 mr-1.5" /> {busy === "test" ? "Testing…" : "Test Connection"}
            </Button>
            <Button variant="outline" onClick={getBouquets} disabled={busy === "bouquets" || !config?.has_api_key} data-testid="ep-bouquets-btn">
              <PackageSearch className="h-4 w-4 mr-1.5" /> {busy === "bouquets" ? "Loading…" : "Get Custom Bouquets"}
            </Button>
          </div>
          {!config?.has_api_key && (
            <p className="mt-3 text-xs text-muted-foreground">Set the API key first to enable these actions.</p>
          )}
          <p className="mt-3 text-xs text-muted-foreground" data-testid="ep-packages-note">
            Note: the provider API can only list your <span className="font-medium">custom bouquets</span>. There is no documented
            endpoint to list normal package IDs — when creating accounts use <code className="font-mono">pack="all"</code> or a specific
            package ID from your ActivationPanel dashboard.
          </p>
          <ResultViewer title="Reseller Information (test connection)" result={testResult} />
          <ResultViewer title="Custom Bouquets" result={bouquets} />
        </CardContent>
      </Card>

      {/* Templates */}
      <Card className="mt-6" data-testid="ep-templates-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><PackageSearch className="h-5 w-5 text-primary" /> Templates</CardTitle>
          <CardDescription>Named ActivationPanel packages/templates. The selected one is sent as the <code className="font-mono">pack</code> value when creating a customer.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {templates.length === 0 ? (
            <p className="text-sm text-muted-foreground">No templates configured.</p>
          ) : (
            <div className="divide-y divide-border">
              {templates.map((t) => (
                <div key={t.id} className="flex items-center gap-3 py-2" data-testid={`ep-tpl-${t.id}`}>
                  <span className="text-sm font-medium">{t.name}</span>
                  <code className="font-mono text-xs text-muted-foreground">#{t.pack_id}</code>
                  {t.is_default && <Badge variant="secondary" className="text-[10px]">Default</Badge>}
                  {isAdmin && (
                    <div className="ml-auto flex items-center gap-1">
                      {!t.is_default && <Button size="sm" variant="ghost" className="h-7" onClick={() => makeDefault(t.id)} data-testid={`ep-tpl-default-${t.id}`}><Star className="h-3.5 w-3.5 mr-1" />Set default</Button>}
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => editTemplate(t)} data-testid={`ep-tpl-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => deleteTemplate(t.id)} data-testid={`ep-tpl-del-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {isAdmin && (
            <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-border">
              <div className="space-y-1">
                <Label className="text-xs">Template name</Label>
                <Input value={tplForm.name} onChange={(e) => setTplForm((f) => ({ ...f, name: e.target.value }))} placeholder="Balkan" className="h-9 w-40" data-testid="ep-tpl-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Package/Template ID</Label>
                <Input value={tplForm.pack_id} onChange={(e) => setTplForm((f) => ({ ...f, pack_id: e.target.value }))} placeholder="38455" className="h-9 w-36 font-mono" data-testid="ep-tpl-pack" />
              </div>
              <Button onClick={addTemplate} className="h-9" data-testid="ep-tpl-add">
                {tplForm.id ? <><Pencil className="h-4 w-4 mr-1.5" />Update</> : <><Plus className="h-4 w-4 mr-1.5" />Add</>}
              </Button>
              {tplForm.id && <Button variant="ghost" className="h-9" onClick={() => setTplForm({ id: "", name: "", pack_id: "" })}>Cancel</Button>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Accounts created through StreamCore */}
      <Card className="mt-6" data-testid="ep-accounts-card">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg"><Users className="h-5 w-5 text-primary" /> Accounts (created via StreamCore)</CardTitle>
            <CardDescription>Customer M3U URL is masked behind your StreamCore DNS. Provider password is never shown.</CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={loadAccounts} data-testid="ep-refresh-accounts-btn"><RefreshCw className="h-4 w-4" /></Button>
        </CardHeader>
        <CardContent>
          {accounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No accounts created yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground border-b border-border">
                  <tr className="[&>th]:text-left [&>th]:py-2 [&>th]:pr-3">
                    <th>Username</th><th>Template</th><th>Sub</th><th>Created</th><th>M3U URL</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr key={a.token} className="border-b border-border/50 [&>td]:py-2 [&>td]:pr-3" data-testid={`ep-acc-${a.token}`}>
                      <td className="font-mono text-xs">{a.username}</td>
                      <td>{a.template_name || <span className="font-mono text-xs text-muted-foreground">#{a.pack}</span>}</td>
                      <td>{a.sub === "99" ? "Demo" : `${a.sub}m`}</td>
                      <td className="text-xs text-muted-foreground">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <code className="font-mono text-[11px] break-all max-w-[280px] truncate">{a.streamcore_url}</code>
                          {a.streamcore_url && <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={async () => { (await copyToClipboard(a.streamcore_url)) ? toast.success("Copied") : toast.error("Copy failed"); }}><Copy className="h-3.5 w-3.5" /></Button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Logs */}
      <Card className="mt-6" data-testid="ep-logs-card">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg"><ScrollText className="h-5 w-5 text-primary" /> API Request Log</CardTitle>
            <CardDescription>Recent requests (the API key is never logged).</CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={loadLogs} data-testid="ep-refresh-logs-btn"><RefreshCw className="h-4 w-4" /></Button>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No requests yet.</p>
          ) : (
            <div className="divide-y divide-border">
              {logs.map((l) => (
                <div key={l.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`ep-log-${l.id}`}>
                  {l.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <XCircle className="h-4 w-4 text-destructive shrink-0" />}
                  <code className="font-mono text-xs">{l.action}</code>
                  {l.http_status && <Badge variant="outline" className="text-[10px]">HTTP {l.http_status}</Badge>}
                  {l.error && <span className="text-xs text-destructive truncate">{l.error}</span>}
                  <span className="ml-auto text-xs text-muted-foreground shrink-0">{new Date(l.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
